import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { bugsnagNotify } from 'src/lib/bugsnag';

export const useDebouncingLoader = ({ loadOptions, debounceTimeout, eagerOptionsLoad }) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [error, setError] = useState(null);
  const [debouncePending, setDebouncePending] = useState(false);

  const currentLoader = useRef(0);

  const doLoadOptions = useCallback(async (text) => {
    setLoading(true);
    setError(false);
    setDebouncePending(false);

    const loadingId = Math.random();
    currentLoader.current = loadingId;
    try {
      const ops = await loadOptions(text);
      if (currentLoader.current !== loadingId) return;
      setOptions(ops);
    } catch (e) {
      bugsnagNotify(e);
      if (currentLoader.current !== loadingId) return;
      setError(e);
    }
    setLoading(false);
  }, [loadOptions]);


  const debounceTimer = useRef(0);
  const onFilterChange = useCallback((filter, isImmediate) => {
    let timeout = debounceTimeout;
    if (isNaN(timeout)) timeout = 1000;
    if (isImmediate) timeout = 0;
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => { doLoadOptions(filter); }, timeout);
    setDebouncePending(true);
  }, [debounceTimeout, doLoadOptions]);

  const cancelOptionsLoading = useCallback(() => {
    clearTimeout(debounceTimer.current);
    setLoading(false);
    setDebouncePending(false);
    currentLoader.current = Math.random();
  }, []);

  useLayoutEffect(() => {
    if (eagerOptionsLoad) {
      doLoadOptions('');
    }
  }, [doLoadOptions, eagerOptionsLoad]);

  return {
    onFilterChange,
    cancelOptionsLoading,
    loading: loading || debouncePending,
    options,
    error
  };
};
